import { type Quaternion, type Vector2, Vector3 } from '@technology/web3d';
import type { PanInput } from './path-navigation-math.types';
import { Vector3Const } from './web3d-internals';

export const SNAPPING_FORWARD_DISTANCE = 10;
export const SNAPPING_HEIGHT_ABOVE_ALIGNMENT = 4;

/**
 * Determine navigation direction (1 = forward, -1 = backward) by comparing
 * the camera's forward vector with the alignment's forward vector.
 */
export function computeAlignmentDirection(
  cameraQuat: Quaternion,
  alignmentQuat: Quaternion
): 1 | -1 {
  const camForward = new Vector3(0, 0, -1).applyQuaternion(cameraQuat);
  const alignForward = new Vector3(0, 0, -1).applyQuaternion(alignmentQuat);
  return camForward.dot(alignForward) >= 0 ? 1 : -1;
}

/** Whether the camera faces against the alignment direction. */
export function isLookingBackwards(
  cameraQuat: Quaternion,
  alignmentQuat: Quaternion
): boolean {
  return computeAlignmentDirection(cameraQuat, alignmentQuat) === -1;
}

/** Camera position used when snapping to an alignment: behind the target and above it. */
export function computeSnapCameraPosition(
  target: Vector3,
  forwardOnAlignment: Vector3,
  lookingBackwards: boolean
): Vector3 {
  return target
    .clone()
    .addScaledVector(
      forwardOnAlignment,
      SNAPPING_FORWARD_DISTANCE * (lookingBackwards ? 1 : -1)
    )
    .addScaledVector(Vector3Const.up, SNAPPING_HEIGHT_ABOVE_ALIGNMENT);
}

/**
 * How far a pan drag moves the camera: in the screen plane in cross-section view,
 * otherwise across the alignment and up, so the camera never drifts along it.
 */
export function computePanOffset(input: PanInput): Vector3 {
  const reference = input.crossSection
    ? input.cameraQuaternion
    : input.alignmentQuaternion;
  const offset = new Vector3(0, 1, 0)
    .applyQuaternion(reference)
    .multiplyScalar(input.deltaY * input.panSpeed);
  if (input.verticalOnly) return offset;

  // Dragging right moves the scene right, so the camera moves left on screen.
  const horizontalSpeed =
    !input.crossSection && input.lookingBackwards
      ? -input.panSpeed
      : input.panSpeed;
  return offset.add(
    new Vector3(1, 0, 0)
      .applyQuaternion(reference)
      .multiplyScalar(-input.deltaX * horizontalSpeed)
  );
}

/**
 * Orbits a spherical direction (x: polar angle from up, y: azimuth) by a pointer delta,
 * keeping the camera from flipping over the poles.
 */
export function applyOrbitDelta(
  spherical: Vector2,
  delta: Vector2,
  rotationSpeed: Vector2,
  poleMargin: number
): Vector2 {
  spherical.x = Math.max(
    poleMargin,
    Math.min(spherical.x + delta.y * rotationSpeed.y, Math.PI - poleMargin)
  );
  spherical.y -= delta.x * rotationSpeed.x;
  return spherical;
}
